<script lang="ts">
  import { onMount } from 'svelte';
  import gsap from 'gsap';
  
  // Свойства компонента
  export let mouseX: number = 0;
  export let mouseY: number = 0;
  export let isBlind: boolean = false;
  
  // Переменные для позиции и анимации глаза
  let eyeX = 0;
  let eyeY = 0;
  let eyeContainer: HTMLDivElement;
  let scrollPosition = 0;
  let followMouse = true;
  let isScrolled = false;
  let lastMoveTime = Date.now();
  let isHovering = false;
  let rayContainer: HTMLDivElement;
  
  // Отслеживание позиции скролла
  function handleScroll() {
    scrollPosition = window.scrollY;
    
    // Определяем, когда глаз должен переместиться
    if (scrollPosition > 100) {
      if (!isScrolled) {
        isScrolled = true;
        animateEyeToCorner();
      }
    } else {
      if (isScrolled) {
        isScrolled = false;
        animateEyeToCenter();
      }
    }
  }
  
  // Функция для обнаружения hover над интерактивными элементами
  function handleMouseMove(event: MouseEvent) {
    mouseX = event.clientX;
    mouseY = event.clientY;
    lastMoveTime = Date.now();
    
    // Проверяем, находится ли мышь над интерактивным элементом
    const target = event.target as HTMLElement;
    const isInteractive = 
      target.tagName === 'A' || 
      target.tagName === 'BUTTON' || 
      target.classList.contains('btn') ||
      target.closest('a') !== null ||
      target.closest('button') !== null ||
      target.closest('.btn') !== null;
    
    if (isInteractive && !isHovering) {
      isHovering = true;
      // Усилим реакцию глаза при наведении на интерактивный элемент
      gsap.to('.eye-pupil', { scale: 1.2, duration: 0.3 });
    } else if (!isInteractive && isHovering) {
      isHovering = false;
      gsap.to('.eye-pupil', { scale: 1, duration: 0.3 });
    }
  }
  
  // Обработчик клика для создания луча
  function handleClick(event: MouseEvent) {
    if (isBlind || !followMouse) return;
    
    const clickX = event.clientX;
    const clickY = event.clientY;
    
    // Определяем центр глаза
    const rect = eyeContainer.getBoundingClientRect();
    const eyeCenterX = rect.left + rect.width / 2;
    const eyeCenterY = rect.top + rect.height / 2;
    
    // Рассчитываем угол между центром глаза и точкой клика
    const angle = Math.atan2(clickY - eyeCenterY, clickX - eyeCenterX);
    const distance = Math.sqrt(Math.pow(clickX - eyeCenterX, 2) + Math.pow(clickY - eyeCenterY, 2));
    
    // Создаем луч
    createRay(eyeCenterX, eyeCenterY, angle, distance);
  }
  
  // Создание луча от глаза до точки клика
  function createRay(startX: number, startY: number, angle: number, distance: number) {
    // Удаляем предыдущий луч, если он есть
    if (rayContainer.firstChild) {
      rayContainer.innerHTML = '';
    }
    
    // Создаем новый луч
    const ray = document.createElement('div');
    ray.className = 'eye-ray';
    
    // Позиционируем луч
    ray.style.top = `${startY}px`;
    ray.style.left = `${startX}px`;
    ray.style.width = `${distance}px`;
    ray.style.transform = `rotate(${angle}rad)`;
    ray.style.transformOrigin = '0 50%';
    
    // Добавляем луч на страницу
    rayContainer.appendChild(ray);
    
    // Анимируем появление и исчезновение луча
    gsap.fromTo(ray, 
      { opacity: 0, scaleY: 0 }, 
      { 
        opacity: 1, 
        scaleY: 1, 
        duration: 0.15, 
        ease: "power2.out",
        onComplete: () => {
          // Эффект пульсации после появления
          gsap.to(ray, {
            opacity: 0.7,
            scaleY: 0.85,
            duration: 0.1,
            repeat: 1,
            yoyo: true,
            ease: "power1.inOut",
            onComplete: () => {
              // Затем исчезновение
              gsap.to(ray, {
                opacity: 0,
                scaleY: 0.5,
                duration: 0.3,
                delay: 0.15,
                ease: "power3.in",
                onComplete: () => ray.remove()
              });
            }
          });
        }
      }
    );
    
    // Эффект "пульса" из глаза
    gsap.to('.eye-outer', {
      boxShadow: '0 0 60px rgba(255, 69, 0, 1)',
      duration: 0.2,
      repeat: 1,
      yoyo: true
    });
    
    // Эффект сужения зрачка
    gsap.to('.eye-pupil', {
      scale: 0.7,
      duration: 0.1,
      onComplete: () => {
        gsap.to('.eye-pupil', {
          scale: 1,
          duration: 0.2,
          delay: 0.1
        });
      }
    });
  }
  
  // Анимация перемещения глаза в верхний левый угол
  function animateEyeToCorner() {
    gsap.to('.eye-container', {
      top: '20px',
      left: '20px',
      transform: 'none',
      width: '60px',
      height: '60px',
      duration: 0.5,
      ease: "power3.out"
    });
    
    // Уменьшаем интенсивность эффектов для маленького глаза
    gsap.to('.flame-container', {
      width: '200%',
      height: '200%',
      duration: 0.5
    });
    
    gsap.to('.eye-outer', {
      boxShadow: '0 0 20px rgba(255, 69, 0, 0.6)',
      duration: 0.5
    });
  }
  
  // Анимация возвращения глаза в центр
  function animateEyeToCenter() {
    gsap.to('.eye-container', {
      top: '30%',  // Ещё выше поднимаем глаз
      left: '50%',
      transform: 'translateX(-50%)',
      width: '180px',
      height: '180px',
      duration: 0.5,
      ease: "power3.out"
    });
    
    // Восстанавливаем интенсивность эффектов для большого глаза
    gsap.to('.flame-container', {
      width: '240%',
      height: '240%',
      duration: 0.5
    });
    
    gsap.to('.eye-outer', {
      boxShadow: '0 0 40px rgba(255, 69, 0, 0.8)',
      duration: 0.5
    });
  }
  
  // Обновление позиции глаза при изменении координат мыши
  $: if (eyeContainer && followMouse) {
    const rect = eyeContainer.getBoundingClientRect();
    const eyeCenterX = rect.left + rect.width / 2;
    const eyeCenterY = rect.top + rect.height / 2;
    
    // Рассчитываем угол между центром глаза и мышью
    const angle = Math.atan2(mouseY - eyeCenterY, mouseX - eyeCenterX);
    
    // Увеличиваем максимальное расстояние для более заметного движения
    // При маленьком размере глаза уменьшаем амплитуду движения зрачка
    const maxDistance = isScrolled ? 10 : 30;
    
    // Добавляем интенсивность движения при наведении на интерактивные элементы
    const intensityMultiplier = isHovering ? 1.5 : 1;
    
    eyeX = Math.cos(angle) * maxDistance * intensityMultiplier;
    eyeY = Math.sin(angle) * maxDistance * intensityMultiplier;
  }
  
  // Реакция на изменение состояния VPN (слепой/зрячий)
  $: if (eyeContainer) {
    if (isBlind) {
      // Анимация закрытия глаза
      gsap.to('.eye-pupil', { 
        opacity: 0.2, 
        scale: 0.7, 
        backgroundColor: 'var(--shire-green)',
        duration: 0.5 
      });
      gsap.to('.eye-iris', { 
        backgroundColor: 'var(--shire-light)', 
        borderColor: 'var(--shire-green)',
        duration: 0.5 
      });
      gsap.to('.flame', { opacity: 0, height: '0%', duration: 0.5 });
      
      // Останавливаем следование за мышью
      followMouse = false;
    } else {
      // Анимация открытия глаза
      gsap.to('.eye-pupil', { 
        opacity: 1, 
        scale: 1, 
        backgroundColor: 'black',
        duration: 0.5 
      });
      gsap.to('.eye-iris', { 
        backgroundColor: 'var(--sauron-orange)', 
        borderColor: 'var(--sauron-red)',
        duration: 0.5 
      });
      gsap.to('.flame', { opacity: 1, height: '100%', duration: 0.5 });
      
      // Возобновляем следование за мышью
      followMouse = true;
    }
  }
  
  // Случайное движение зрачка для "живого" эффекта
  let jitterInterval: number;
  let focusInterval: number;
  
  $: if (eyeContainer && !isBlind) {
    clearInterval(jitterInterval);
    jitterInterval = setInterval(() => {
      // Проверяем, двигалась ли мышь недавно
      const idleTime = Date.now() - lastMoveTime;
      
      // Если мышь не двигалась более 3 секунд, добавляем случайные движения
      if (idleTime > 3000 && followMouse) {
        const jitterAmount = isScrolled ? 3 : 8;
        const jitterX = (Math.random() - 0.5) * jitterAmount;
        const jitterY = (Math.random() - 0.5) * jitterAmount;
        
        gsap.to('.eye-iris', {
          x: eyeX + jitterX,
          y: eyeY + jitterY,
          duration: 0.8,
          ease: "power1.inOut"
        });
      }
    }, 2000);
  }
  
  onMount(() => {
    // Регистрируем обработчики событий
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('click', handleClick);
    
    // Создаем контейнер для лучей
    rayContainer = document.createElement('div');
    rayContainer.className = 'ray-container';
    document.body.appendChild(rayContainer);
    
    // Устанавливаем начальное положение глаза (выше, чем было)
    gsap.set('.eye-container', {
      top: '30%'
    });
    
    // Начальная анимация появления глаза
    gsap.from('.eye-container', { 
      opacity: 0, 
      scale: 0, 
      duration: 1, 
      ease: "elastic.out(1, 0.5)" 
    });
    
    // Пульсация глаза
    gsap.to('.eye-iris', { 
      scale: 1.05, 
      repeat: -1, 
      yoyo: true, 
      duration: 2, 
      ease: "sine.inOut" 
    });
    
    // Добавляем случайное мигание глаза
    const blinkEye = () => {
      if (isBlind) return;
      
      gsap.to('.eye-pupil', {
        scaleY: 0.1,
        duration: 0.1,
        repeat: 1,
        yoyo: true,
        ease: "power2.inOut",
        onComplete: () => {
          // Определяем случайное время до следующего моргания (от 3 до 8 секунд)
          const nextBlink = 3000 + Math.random() * 5000;
          setTimeout(blinkEye, nextBlink);
        }
      });
    };
    
    // Инициируем первое моргание через случайное время
    setTimeout(blinkEye, 3000 + Math.random() * 5000);
    
    // Создаем эффект пламени для ободка глаза
    gsap.to('.eye-outer', {
      boxShadow: '0 0 40px rgba(255, 69, 0, 0.9)',
      repeat: -1,
      yoyo: true,
      duration: 2,
      ease: "sine.inOut"
    });
    
    // Периодически фокусируем взгляд на разных частях экрана
    focusInterval = setInterval(() => {
      // Только если мышь была неактивна долгое время
      if (Date.now() - lastMoveTime > 5000 && followMouse && !isHovering) {
        // Случайная точка на экране для фокусировки
        const screenX = Math.random() * window.innerWidth;
        const screenY = Math.random() * window.innerHeight;
        
        // Медленно перемещаем взгляд туда
        const rect = eyeContainer.getBoundingClientRect();
        const eyeCenterX = rect.left + rect.width / 2;
        const eyeCenterY = rect.top + rect.height / 2;
        
        const angle = Math.atan2(screenY - eyeCenterY, screenX - eyeCenterX);
        const maxDistance = isScrolled ? 10 : 30;
        
        const targetX = Math.cos(angle) * maxDistance;
        const targetY = Math.sin(angle) * maxDistance;
        
        gsap.to('.eye-iris', {
          x: targetX,
          y: targetY,
          duration: 1.5,
          ease: "power2.inOut"
        });
      }
    }, 7000);
    
    return () => {
      clearInterval(jitterInterval);
      clearInterval(focusInterval);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('click', handleClick);
      
      // Удаляем контейнер для лучей при размонтировании компонента
      if (rayContainer && rayContainer.parentNode) {
        rayContainer.parentNode.removeChild(rayContainer);
      }
    };
  });
</script>

<div class="eye-container" bind:this={eyeContainer}>
  <!-- Пламя вокруг глаза -->
  <div class="flame-container">
    <div class="flame"></div>
  </div>
  
  <!-- Глаз -->
  <div class="eye-outer">
    <div class="eye-iris" style="transform: translate({eyeX}px, {eyeY}px)">
      <div class="eye-pupil"></div>
    </div>
  </div>
</div>

<style>
  .eye-container {
    position: fixed;
    top: 30%;  /* Еще выше поднимаем глаз */
    left: 50%;
    transform: translateX(-50%);
    z-index: 100;
    width: 180px;
    height: 180px;
    display: flex;
    justify-content: center;
    align-items: center;
    pointer-events: none; /* Чтобы глаз не перехватывал клики */
    transition: width 0.3s ease, height 0.3s ease;
  }
  
  .flame-container {
    position: absolute;
    width: 240%;
    height: 240%;
    overflow: hidden;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    transition: width 0.3s ease, height 0.3s ease;
  }
  
  .flame {
    position: absolute;
    width: 130%;
    height: 120%;
    background: radial-gradient(
      ellipse at center, 
      rgba(255,69,0,0.9) 0%, 
      rgba(255,140,0,0.7) 40%, 
      rgba(255,215,0,0.4) 70%,
      rgba(255,69,0,0.2) 100%
    );
    filter: blur(8px);
    transform-origin: center center;
    animation: flicker 3s infinite alternate;
  }
  
  .eye-outer {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
    background-color: var(--mordor-darker);
    box-shadow: 0 0 40px rgba(255, 69, 0, 0.8);
    position: relative;
    overflow: hidden;
    transition: box-shadow 0.3s ease;
  }
  
  .eye-outer::before {
    content: "";
    position: absolute;
    top: -10%;
    left: -10%;
    width: 120%;
    height: 120%;
    background: radial-gradient(
      ellipse at center,
      transparent 0%,
      transparent 60%,
      rgba(255, 69, 0, 0.2) 70%,
      rgba(255, 69, 0, 0.4) 100%
    );
  }
  
  .eye-iris {
    width: 70%;
    height: 70%;
    background: radial-gradient(
      ellipse at center,
      #ffa500 0%,
      #ff8c00 30%,
      #ff4500 70%,
      #8b0000 100%
    );
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 4px solid var(--sauron-red);
    transition: transform 0.2s cubic-bezier(0.22, 1, 0.36, 1);
    position: relative;
    overflow: hidden;
  }
  
  .eye-iris::after {
    content: "";
    position: absolute;
    top: -5px;
    left: 30%;
    width: 40%;
    height: 20%;
    background: rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    transform: rotate(-30deg);
  }
  
  .eye-pupil {
    width: 40%;
    height: 40%;
    background: linear-gradient(to bottom, #000000, #1a0000);
    border-radius: 50%;
    transform-origin: center center;
    position: relative;
    transition: transform 0.2s ease, scale 0.3s ease;
  }
  
  .eye-pupil::after {
    content: "";
    position: absolute;
    top: 15%;
    right: 15%;
    width: 30%;
    height: 30%;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
  }
  
  /* Стили для лучей */
  :global(.ray-container) {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 99;
  }
  
  :global(.eye-ray) {
    position: absolute;
    height: 5px;
    background: linear-gradient(90deg, rgba(255,69,0,1) 0%, rgba(255,140,0,0.8) 70%, rgba(255,215,0,0) 100%);
    border-radius: 5px;
    box-shadow: 0 0 10px rgba(255, 69, 0, 0.7);
    opacity: 0;
    transform-origin: 0 50%;
    filter: blur(1px);
  }
  
  @keyframes flicker {
    0% {
      opacity: 1;
      transform: scale(1) rotate(0deg);
    }
    25% {
      opacity: 0.9;
      transform: scale(0.98) rotate(1deg);
    }
    50% {
      opacity: 1;
      transform: scale(1.02) rotate(-1deg);
    }
    75% {
      opacity: 0.9;
      transform: scale(0.95) rotate(2deg);
    }
    100% {
      opacity: 1;
      transform: scale(1.01) rotate(-1deg);
    }
  }
</style> 